import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../services/api';

const METRICS = [
  { key: 'participants_with_balance', label: 'Participants with Balance', format: 'number' },
  { key: 'assets', label: 'Plan Assets', format: 'currency' },
  { key: 'employer_contributions', label: 'Employer Contributions', format: 'currency' },
  { key: 'participant_contributions', label: 'Participant Contributions', format: 'currency' },
  { key: 'total_contributions', label: 'Total Contributions', format: 'currency' },
  { key: 'admin_expenses_total', label: 'Admin Expenses - Total', format: 'currency' },
  { key: 'admin_expense_per_participant', label: 'Admin Expenses - Per Participant', format: 'currency' },
  { key: 'participation_rate', label: 'Participation Ratio', format: 'percent' },
  { key: 'investment_return_pct', label: 'Annual Investment Return %', format: 'percent' },
  { key: 'avg_account_balance', label: 'Average Account Balance', format: 'currency' },
  { key: 'net_cashflow', label: 'Net Cash Flow', format: 'currency' },
  { key: 'cost_bps', label: 'Cost as % of Assets (bps)', format: 'bps' },
  { key: 'employer_match_ratio', label: 'Employer Match Ratio', format: 'percent' },
  { key: 'asset_growth_pct', label: 'Asset Growth Rate %', format: 'percent' },
  { key: 'distributions', label: 'Distributions', format: 'currency' },
];

const SUMMARY_METRIC_MAP = {
  participants_with_balance: { total: 'total_participants', avg: 'avg_participants', median: 'median_participants' },
  assets: { total: 'total_assets', avg: 'avg_assets', median: 'median_assets' },
  employer_contributions: { avg: 'avg_employer_contrib_pp', label_avg: 'Avg Per Participant' },
  participant_contributions: { avg: 'avg_employee_contrib_pp', label_avg: 'Avg Per Participant' },
  total_contributions: { total: 'total_contributions', avg: 'avg_contributions', label_avg: 'Avg Per Participant' },
  admin_expenses_total: { total: 'total_admin_expenses', avg: 'avg_admin_expense_per_plan', label_avg: 'Avg Per Plan' },
  admin_expense_per_participant: { avg: 'avg_admin_expense_pp', median: 'median_admin_expense_pp' },
  participation_rate: { avg: 'avg_participation_rate' },
  investment_return_pct: { avg: 'avg_investment_return' },
  avg_account_balance: { avg: 'avg_account_balance' },
  net_cashflow: { total: 'total_net_cashflow' },
  cost_bps: { avg: 'avg_cost_bps' },
  employer_match_ratio: { avg: 'avg_employer_match_ratio' },
  asset_growth_pct: { avg: 'avg_asset_growth_pct' },
};

const SIZE_CATEGORIES = [
  { value: 'Micro', label: 'Micro (1-9)' },
  { value: 'Small', label: 'Small (10-49)' },
  { value: 'Medium-Small', label: 'Medium-Small (50-249)' },
  { value: 'Medium', label: 'Medium (250-999)' },
  { value: 'Large', label: 'Large (1000+)' },
  { value: 'All', label: 'All Sizes' },
];

// Table style constants
const thStyle = {
  padding: '0.5rem 0.75rem',
  textAlign: 'center',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.15)',
};

const tdStyle = {
  padding: '0.4rem 0.75rem',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '0.8125rem',
  color: '#111827',
  whiteSpace: 'nowrap',
};

const tdStyleNum = {
  ...tdStyle,
  textAlign: 'right',
  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  fontSize: '0.75rem',
};

const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
      if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'bps':
      return `${value.toFixed(1)} bps`;
    case 'number':
      return new Intl.NumberFormat('en-US').format(Math.round(value));
    default:
      return String(value);
  }
};

const formatCellValue = (value, format) => {
  if (value === null || value === undefined) return '';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'bps':
      return `${value.toFixed(1)}`;
    case 'number':
      return new Intl.NumberFormat('en-US').format(Math.round(value));
    default:
      return String(value);
  }
};

// The backend returns annual_data with keys matching our METRICS keys directly,
// so no separate mapping is needed. The selectedMetric key IS the field name.

const CohortAnalysis = () => {
  const navigate = useNavigate();

  // Controls
  const [naicsLevel, setNaicsLevel] = useState(4);
  const [naicsSearch, setNaicsSearch] = useState('');
  const [naicsResults, setNaicsResults] = useState([]);
  const [selectedNaicsList, setSelectedNaicsList] = useState([]);
  const [showNaicsDropdown, setShowNaicsDropdown] = useState(false);
  const [allIndustries, setAllIndustries] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState(['Micro']);
  const [effectiveYear, setEffectiveYear] = useState(2015);

  // Compute comma-separated values for API calls
  const sizeCategory = selectedSizes.includes('All') ? 'All' : selectedSizes.join(',');
  const naicsCodes = allIndustries ? 'All' : selectedNaicsList.map(n => n.code).join(',');
  const hasNaics = allIndustries || selectedNaicsList.length > 0;

  // Data
  const [cohortData, setCohortData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('participants_with_balance');
  const [showIndividualPlans, setShowIndividualPlans] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);

  // NAICS autocomplete
  useEffect(() => {
    if (naicsSearch.length < 2) {
      setNaicsResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await api.searchCohortNaics(naicsSearch, naicsLevel);
        setNaicsResults(results);
      } catch (e) {
        console.error('NAICS search error:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [naicsSearch, naicsLevel]);

  // Preview when selection changes
  const loadPreview = useCallback(async () => {
    if (!hasNaics) {
      setPreview(null);
      return;
    }
    try {
      setPreviewLoading(true);
      const result = await api.getCohortPreview(naicsCodes, sizeCategory, effectiveYear, naicsLevel);
      setPreview(result);
    } catch (e) {
      console.error('Preview error:', e);
    } finally {
      setPreviewLoading(false);
    }
  }, [hasNaics, naicsCodes, sizeCategory, effectiveYear, naicsLevel]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // Load cohort analysis
  const handleAnalyze = async () => {
    if (!hasNaics) return;
    try {
      setLoading(true);
      setError(null);
      setShowIndividualPlans(false);
      setCurrentPage(1);
      const data = await api.getCohortAnalysis(naicsCodes, sizeCategory, effectiveYear, naicsLevel, 1, 50);
      setCohortData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Load page of individual plans
  const loadPage = async (page) => {
    if (!hasNaics || !cohortData) return;
    try {
      setLoading(true);
      const data = await api.getCohortAnalysis(naicsCodes, sizeCategory, effectiveYear, naicsLevel, page, 50);
      setCohortData(prev => ({ ...prev, plans: data.plans, page: data.page, total_pages: data.total_pages }));
      setCurrentPage(page);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNaicsSelect = (item) => {
    // Add to list if not already selected
    if (!selectedNaicsList.find(n => n.code === item.code)) {
      setSelectedNaicsList(prev => [...prev, item]);
    }
    setNaicsSearch('');
    setShowNaicsDropdown(false);
    setCohortData(null);
  };

  const handleNaicsRemove = (code) => {
    setSelectedNaicsList(prev => prev.filter(n => n.code !== code));
    setCohortData(null);
  };

  const years = cohortData?.years || [];
  const summary = cohortData?.summary || {};
  const currentMetric = METRICS.find(m => m.key === selectedMetric);
  const summaryMap = SUMMARY_METRIC_MAP[selectedMetric] || {};

  // Build chart data for retention curve
  const retentionChartData = years.map(yr => ({
    year: yr,
    retention: summary[yr] ? (summary[yr].retention_rate * 100) : null,
    terminated: summary[yr]?.terminated_this_year || 0,
  }));

  // Build chart data for industry dynamics
  const dynamics = cohortData?.industry_dynamics?.by_year || {};
  const dynamicsChartData = years.map(yr => {
    const d = dynamics[yr];
    return {
      year: yr,
      totalPlans: d?.total_plans_in_industry || 0,
      entryRate: d ? (d.entry_rate * 100) : null,
      exitRate: d ? (d.exit_rate * 100) : null,
      netGrowth: d ? (d.net_growth_rate * 100) : null,
      newPlans: d?.new_plans || 0,
      exitedPlans: d?.exited_plans || 0,
    };
  });

  // Build chart data for selected metric trend
  const metricChartData = years.map(yr => {
    const s = summary[yr];
    if (!s) return { year: yr, value: null };
    const avgKey = summaryMap.avg;
    const totalKey = summaryMap.total;
    return {
      year: yr,
      avg: avgKey && s[avgKey] != null ? s[avgKey] : null,
      total: totalKey && s[totalKey] != null ? s[totalKey] : null,
    };
  });

  // CSV export
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (!cohortData || !years.length) return;
    setExporting(true);
    try {
      let csv = 'Metric,' + years.join(',') + '\n';

      // Summary rows
      const summaryRows = [
        { label: 'Plans Filing', key: 'plans_active' },
        { label: 'Plans Surviving', key: 'plans_surviving' },
        { label: 'Retention Rate', key: 'retention_rate' },
        { label: 'Terminated This Year', key: 'terminated_this_year' },
        { label: 'Cumulative Terminated', key: 'cumulative_terminated' },
        { label: 'Annual Churn Rate', key: 'annual_churn_rate' },
      ];
      for (const row of summaryRows) {
        csv += `"${row.label}",` + years.map(yr => summary[yr]?.[row.key] ?? '').join(',') + '\n';
      }

      // Metric-specific summary
      if (summaryMap.total) {
        csv += `"Total",` + years.map(yr => summary[yr]?.[summaryMap.total] ?? '').join(',') + '\n';
      }
      if (summaryMap.avg) {
        csv += `"Average",` + years.map(yr => summary[yr]?.[summaryMap.avg] ?? '').join(',') + '\n';
      }

      // Fetch ALL pages of individual plans
      if (cohortData.total_pages > 0) {
        let allPlans = [];
        for (let p = 1; p <= cohortData.total_pages; p++) {
          const data = await api.getCohortAnalysis(naicsCodes, sizeCategory, effectiveYear, naicsLevel, p, 50);
          if (data.plans) allPlans = allPlans.concat(data.plans);
        }

        csv += '\n';
        csv += '"Plan Name","EIN","Plan #",' + years.join(',') + '\n';
        for (const plan of allPlans) {
          const planName = (plan.plan_name || '').replace(/"/g, '""');
          csv += `"${planName}","${plan.sponsor_ein}","${plan.plan_number}",`;
          csv += years.map(yr => {
            const ad = plan.annual_data?.[yr];
            if (!ad) return '';
            return ad[selectedMetric] ?? '';
          }).join(',') + '\n';
        }
      }

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cohort_${allIndustries ? 'AllIndustries' : naicsCodes.replace(/,/g, '-')}_${sizeCategory}_${effectiveYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const yearRange = [];
  for (let y = 2024; y >= 1990; y--) yearRange.push(y);

  return (
    <div style={{ minHeight: '100vh', background: '#fafbfc' }}>
      {/* Header */}
      <header style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '1rem 0', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em', cursor: 'pointer' }} onClick={() => navigate('/')}>
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay?</a>
              <a href="/admin/research" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Research</a>
              <a href="/admin/prospecting" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Prospecting</a>
              <a href="/admin/blog-writer" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Blog Writer</a>
              <a href="/admin/cohort" style={{ color: '#1e3a8a', textDecoration: 'none', fontSize: '0.9375rem', fontWeight: 600 }}>Cohort Analysis</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero / Controls Section */}
      <section style={{ padding: '2rem 0 1.5rem', background: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', margin: '0 0 0.5rem', letterSpacing: '-0.02em' }}>
            401(k) Cohort Analysis
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem', margin: '0 0 1.5rem' }}>
            Track a cohort of plans from inception through today. Select an industry (or all industries), plan size, and effective year to analyze.
          </p>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* All Industries Toggle */}
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                Scope
              </label>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button onClick={() => { setAllIndustries(false); setCohortData(null); }}
                  style={{
                    padding: '0.5rem 0.75rem', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                    background: !allIndustries ? '#1e3a8a' : 'white',
                    color: !allIndustries ? 'white' : '#374151',
                  }}>
                  By Industry
                </button>
                <button onClick={() => { setAllIndustries(true); setSelectedNaicsList([]); setNaicsSearch(''); setCohortData(null); }}
                  style={{
                    padding: '0.5rem 0.75rem', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                    background: allIndustries ? '#1e3a8a' : 'white',
                    color: allIndustries ? 'white' : '#374151',
                  }}>
                  All Industries
                </button>
              </div>
            </div>

            {/* NAICS Level Toggle — hidden when All Industries */}
            {!allIndustries && (
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                  NAICS Level
                </label>
                <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                  {[4, 6].map(lvl => (
                    <button key={lvl} onClick={() => { setNaicsLevel(lvl); setSelectedNaicsList([]); setNaicsSearch(''); setCohortData(null); }}
                      style={{
                        padding: '0.5rem 1rem', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                        background: naicsLevel === lvl ? '#1e3a8a' : 'white',
                        color: naicsLevel === lvl ? 'white' : '#374151',
                      }}>
                      {lvl}-digit
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* NAICS Search — hidden when All Industries */}
            {!allIndustries && (
              <div style={{ position: 'relative', flex: 1, minWidth: '280px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                  Industry (NAICS Code)
                </label>
                <input
                  type="text"
                  value={naicsSearch}
                  onChange={(e) => { setNaicsSearch(e.target.value); setShowNaicsDropdown(true); setCohortData(null); }}
                  onFocus={() => naicsResults.length > 0 && setShowNaicsDropdown(true)}
                  onBlur={() => setTimeout(() => setShowNaicsDropdown(false), 200)}
                  placeholder="Search by name or code (e.g. Physician, 6211)..."
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px',
                    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
                  }}
                />
                {showNaicsDropdown && naicsResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
                    border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '300px', overflowY: 'auto', zIndex: 50,
                  }}>
                    {naicsResults
                      .filter(item => !selectedNaicsList.find(n => n.code === item.code))
                      .map((item, i) => (
                      <div key={i} onClick={() => handleNaicsSelect(item)}
                        style={{
                          padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem',
                          borderBottom: '1px solid #f3f4f6',
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#eff6ff'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}>
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{item.code}</span>
                        <span style={{ color: '#374151', marginLeft: '0.5rem' }}>{item.description}</span>
                        {item.sub_codes && <span style={{ color: '#9ca3af', marginLeft: '0.5rem', fontSize: '0.75rem' }}>({item.sub_codes} sub-codes)</span>}
                      </div>
                    ))}
                  </div>
                )}
                {/* Selected NAICS Chips */}
                {selectedNaicsList.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.375rem' }}>
                    {selectedNaicsList.map(n => (
                      <span key={n.code} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        padding: '0.2rem 0.5rem', background: '#eff6ff', border: '1px solid #bfdbfe',
                        borderRadius: '9999px', fontSize: '0.75rem', color: '#1e3a8a', fontWeight: 500,
                      }}>
                        {n.code} — {n.description}
                        <button onClick={() => handleNaicsRemove(n.code)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                            fontSize: '0.875rem', lineHeight: 1, padding: '0 0.125rem', fontWeight: 700,
                          }}
                          title="Remove">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Size Category — Multi-select */}
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                Plan Size
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {SIZE_CATEGORIES.map(s => {
                  const isSelected = s.value === 'All'
                    ? selectedSizes.includes('All')
                    : selectedSizes.includes(s.value) && !selectedSizes.includes('All');
                  return (
                    <button key={s.value}
                      onClick={() => {
                        if (s.value === 'All') {
                          setSelectedSizes(['All']);
                        } else {
                          setSelectedSizes(prev => {
                            const without_all = prev.filter(v => v !== 'All');
                            const has = without_all.includes(s.value);
                            const next = has
                              ? without_all.filter(v => v !== s.value)
                              : [...without_all, s.value];
                            return next.length === 0 ? ['All'] : next;
                          });
                        }
                        setCohortData(null);
                      }}
                      style={{
                        padding: '0.375rem 0.625rem', border: 'none', cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: isSelected ? 600 : 400,
                        borderRadius: '6px',
                        background: isSelected ? '#1e3a8a' : '#f3f4f6',
                        color: isSelected ? 'white' : '#374151',
                      }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Effective Year */}
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                Effective Year
              </label>
              <select value={effectiveYear} onChange={(e) => { setEffectiveYear(parseInt(e.target.value)); setCohortData(null); }}
                style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', background: 'white' }}>
                {yearRange.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Analyze Button */}
            <button onClick={handleAnalyze} disabled={!hasNaics || loading}
              style={{
                padding: '0.5rem 1.5rem', background: hasNaics ? '#1e3a8a' : '#9ca3af', color: 'white',
                border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600, cursor: hasNaics ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}>
              {loading ? 'Analyzing...' : 'Analyze Cohort'}
            </button>
          </div>

          {/* Preview */}
          {preview && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              {previewLoading ? 'Counting plans...' : (
                <span>
                  Found <strong style={{ color: '#1e3a8a' }}>{preview.plan_count.toLocaleString()}</strong> plans matching criteria
                  {preview.plan_count > 10000 && <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>(very large cohort - analysis may take 30+ seconds)</span>}
                  {preview.plan_count > 2000 && preview.plan_count <= 10000 && <span style={{ color: '#f59e0b', marginLeft: '0.5rem' }}>(large cohort - analysis may take a moment)</span>}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Error */}
      {error && (
        <div style={{ maxWidth: '1400px', margin: '1rem auto', padding: '0 2.5rem' }}>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '1rem', color: '#dc2626' }}>
            Error: {error}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #1e3a8a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto' }}></div>
          <p style={{ color: '#6b7280', marginTop: '1rem' }}>Analyzing cohort...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Results */}
      {cohortData && !loading && cohortData.total_plans > 0 && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1.5rem 2.5rem' }}>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Total Plans', value: cohortData.total_plans.toLocaleString() },
              { label: 'Not Terminated', value: summary[years[years.length - 1]]?.plans_surviving?.toLocaleString() || '0', sub: `of ${cohortData.total_plans}` },
              { label: 'Latest Retention', value: `${((summary[years[years.length - 1]]?.retention_rate || 0) * 100).toFixed(1)}%` },
              { label: 'Avg Annual Churn', value: `${((cohortData.avg_annual_churn || 0) * 100).toFixed(1)}%` },
              { label: 'Median Lifetime', value: cohortData.median_lifetime_years != null ? `${cohortData.median_lifetime_years} yr` : '> ' + (years.length) + ' yr', sub: 'years to 50% retention' },
              { label: 'Avg Assets (Latest)', value: formatValue(summary[years[years.length - 1]]?.avg_assets, 'currency') },
            ].map((card, i) => (
              <div key={i} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>{card.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginTop: '0.25rem' }}>{card.value}</div>
                {card.sub && <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{card.sub}</div>}
              </div>
            ))}
          </div>

          {/* Metric Selector */}
          <div style={{ marginBottom: '1rem', overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '0.5rem' }}>
            {METRICS.map(m => (
              <button key={m.key} onClick={() => setSelectedMetric(m.key)}
                style={{
                  padding: '0.375rem 0.75rem', marginRight: '0.375rem', marginBottom: '0.375rem',
                  border: selectedMetric === m.key ? '2px solid #1e3a8a' : '1px solid #d1d5db',
                  borderRadius: '6px', fontSize: '0.75rem', fontWeight: selectedMetric === m.key ? 600 : 400, cursor: 'pointer',
                  background: selectedMetric === m.key ? '#eff6ff' : 'white',
                  color: selectedMetric === m.key ? '#1e3a8a' : '#374151',
                  display: 'inline-block',
                }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Large cohort notice */}
          {cohortData?.is_large_cohort && (
            <div style={{ marginBottom: '1rem', padding: '0.625rem 0.75rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '0.8125rem', color: '#92400e' }}>
              <strong>Large cohort ({cohortData.total_plans.toLocaleString()} plans):</strong> Showing summary statistics only.
              Individual plan drill-down and median calculations are skipped for performance. Use a specific NAICS code to see individual plans.
            </div>
          )}

          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                {currentMetric?.label || 'Participants with Balance'}
              </h2>
              {!cohortData?.is_large_cohort && (
                <button onClick={() => setShowIndividualPlans(!showIndividualPlans)}
                  style={{
                    padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px',
                    fontSize: '0.75rem', cursor: 'pointer', background: showIndividualPlans ? '#eff6ff' : 'white',
                    color: showIndividualPlans ? '#1e3a8a' : '#374151', fontWeight: showIndividualPlans ? 600 : 400,
                  }}>
                  {showIndividualPlans ? 'Hide Individual Plans' : 'Show Individual Plans'}
                </button>
              )}
            </div>
            <button onClick={handleExport} disabled={exporting}
              style={{ padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.75rem', cursor: exporting ? 'wait' : 'pointer', background: 'white', opacity: exporting ? 0.6 : 1 }}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>

          {/* Summary Waterfall Table */}
          <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: '#1e3a8a' }}>
                  <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2, background: '#1e3a8a', minWidth: '160px' }}>Metric</th>
                  {years.map(yr => <th key={yr} style={thStyle}>{yr}</th>)}
                </tr>
              </thead>
              <tbody>
                {/* Summary metric rows */}
                {summaryMap.total && (
                  <tr style={{ background: '#f0f9ff' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: '#f0f9ff' }}>Total</td>
                    {years.map(yr => (
                      <td key={yr} style={tdStyleNum}>
                        {formatValue(summary[yr]?.[summaryMap.total], currentMetric?.format)}
                      </td>
                    ))}
                  </tr>
                )}
                {summaryMap.avg && (
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: 'white' }}>
                      {summaryMap.label_avg || 'Average'}
                    </td>
                    {years.map(yr => (
                      <td key={yr} style={tdStyleNum}>
                        {formatValue(summary[yr]?.[summaryMap.avg], currentMetric?.format)}
                      </td>
                    ))}
                  </tr>
                )}
                {summaryMap.median && (
                  <tr style={{ background: '#f9fafb' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb' }}>Median</td>
                    {years.map(yr => (
                      <td key={yr} style={tdStyleNum}>
                        {formatValue(summary[yr]?.[summaryMap.median], currentMetric?.format)}
                      </td>
                    ))}
                  </tr>
                )}
                {/* Always show operational rows */}
                <tr style={{ borderTop: '2px solid #1e3a8a' }}>
                  <td style={{ ...tdStyle, fontWeight: 700, position: 'sticky', left: 0, background: 'white' }}>Plans Filing</td>
                  {years.map(yr => <td key={yr} style={tdStyleNum}>{summary[yr]?.plans_active?.toLocaleString() || ''}</td>)}
                </tr>
                <tr style={{ background: '#f0f9ff' }}>
                  <td style={{ ...tdStyle, fontWeight: 700, position: 'sticky', left: 0, background: '#f0f9ff' }}>Plans Surviving</td>
                  {years.map(yr => <td key={yr} style={{ ...tdStyleNum, fontWeight: 600 }}>{summary[yr]?.plans_surviving?.toLocaleString() || ''}</td>)}
                </tr>
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb' }}>Retention Rate</td>
                  {years.map(yr => (
                    <td key={yr} style={tdStyleNum}>
                      {summary[yr] ? `${(summary[yr].retention_rate * 100).toFixed(1)}%` : ''}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: 'white' }}>Terminated This Year</td>
                  {years.map(yr => (
                    <td key={yr} style={{ ...tdStyleNum, color: (summary[yr]?.terminated_this_year || 0) > 0 ? '#dc2626' : '#111827' }}>
                      {summary[yr]?.terminated_this_year || 0}
                    </td>
                  ))}
                </tr>
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb' }}>Cumulative Terminated</td>
                  {years.map(yr => (
                    <td key={yr} style={tdStyleNum}>
                      {summary[yr]?.cumulative_terminated || 0}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: 'white' }}>Annual Churn Rate</td>
                  {years.map(yr => (
                    <td key={yr} style={{ ...tdStyleNum, color: (summary[yr]?.annual_churn_rate || 0) > (cohortData?.avg_annual_churn || 0) ? '#dc2626' : '#111827' }}>
                      {summary[yr]?.annual_churn_rate != null ? `${(summary[yr].annual_churn_rate * 100).toFixed(1)}%` : ''}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Individual Plans Waterfall Table */}
          {showIndividualPlans && cohortData.plans && cohortData.plans.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: '0 0 0.75rem' }}>
                Individual Plans — {currentMetric?.label}
                <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.875rem', marginLeft: '0.75rem' }}>
                  Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, cohortData.total_plans)} of {cohortData.total_plans.toLocaleString()}
                </span>
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: '#1e3a8a' }}>
                      <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2, background: '#1e3a8a', minWidth: '200px', textAlign: 'left' }}>Plan Name</th>
                      <th style={{ ...thStyle, minWidth: '90px' }}>EIN</th>
                      <th style={{ ...thStyle, minWidth: '40px' }}>#</th>
                      {years.map(yr => <th key={yr} style={thStyle}>{yr}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cohortData.plans.map((plan, idx) => {
                      const isFinal = plan.is_terminated;
                      const lastYear = plan.last_filing_year;
                      return (
                        <tr key={`${plan.sponsor_ein}-${plan.plan_number}`}
                          style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{
                            ...tdStyle,
                            position: 'sticky', left: 0, zIndex: 1,
                            background: idx % 2 === 0 ? 'white' : '#f9fafb',
                            maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis',
                            fontWeight: 500,
                            color: isFinal ? '#dc2626' : '#111827',
                          }}
                            title={plan.plan_name}>
                            {plan.plan_name || 'Unknown Plan'}
                          </td>
                          <td style={{ ...tdStyleNum, fontSize: '0.6875rem' }}>{plan.sponsor_ein}</td>
                          <td style={{ ...tdStyleNum, textAlign: 'center' }}>{plan.plan_number}</td>
                          {years.map(yr => {
                            const ad = plan.annual_data?.[yr];
                            const isLastFilingYear = lastYear && yr === lastYear && isFinal;
                            const isAfterTermination = lastYear && yr > lastYear && isFinal;

                            if (isAfterTermination) {
                              return (
                                <td key={yr} style={{ ...tdStyleNum, background: '#f3f4f6', color: '#d1d5db' }}>
                                  —
                                </td>
                              );
                            }

                            const cellValue = ad ? ad[selectedMetric] : null;
                            return (
                              <td key={yr} style={{
                                ...tdStyleNum,
                                ...(isLastFilingYear ? {
                                  color: '#dc2626',
                                  background: '#fef2f2',
                                  fontWeight: 600,
                                } : {}),
                              }}>
                                {cellValue != null ? formatCellValue(cellValue, currentMetric?.format) : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {cohortData.total_pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => loadPage(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                    style={{
                      padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px',
                      fontSize: '0.8125rem', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                      background: currentPage <= 1 ? '#f9fafb' : 'white', color: currentPage <= 1 ? '#9ca3af' : '#374151',
                    }}>
                    ← Previous
                  </button>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                    Page {currentPage} of {cohortData.total_pages}
                  </span>
                  <button
                    onClick={() => loadPage(currentPage + 1)}
                    disabled={currentPage >= cohortData.total_pages || loading}
                    style={{
                      padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px',
                      fontSize: '0.8125rem', cursor: currentPage >= cohortData.total_pages ? 'not-allowed' : 'pointer',
                      background: currentPage >= cohortData.total_pages ? '#f9fafb' : 'white',
                      color: currentPage >= cohortData.total_pages ? '#9ca3af' : '#374151',
                    }}>
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Charts Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>

            {/* Retention Curve */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.25rem' }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#111827', margin: '0 0 1rem' }}>Retention Curve</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={retentionChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={(v) => [`${v?.toFixed(1)}%`, 'Retention']} />
                  <Line type="monotone" dataKey="retention" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3 }} name="Retention %" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Plans Terminated Per Year */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.25rem' }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#111827', margin: '0 0 1rem' }}>Plans Terminated Per Year</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={retentionChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="terminated" fill="#dc2626" name="Terminated" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Metric Trend Chart */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.25rem', gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#111827', margin: '0 0 1rem' }}>
                {currentMetric?.label} — Trend Over Time
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metricChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }}
                    tickFormatter={v => {
                      if (currentMetric?.format === 'currency') {
                        if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(0)}B`;
                        if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
                        if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
                        return `$${v}`;
                      }
                      if (currentMetric?.format === 'percent') return `${(v * 100).toFixed(0)}%`;
                      if (currentMetric?.format === 'bps') return `${v.toFixed(0)}`;
                      return v.toLocaleString();
                    }}
                  />
                  <Tooltip
                    formatter={(v, name) => {
                      if (v == null) return ['-', name];
                      return [formatValue(v, currentMetric?.format), name === 'avg' ? 'Average' : 'Total'];
                    }}
                  />
                  <Legend formatter={(val) => val === 'avg' ? 'Average' : 'Total'} />
                  {summaryMap.avg && (
                    <Line type="monotone" dataKey="avg" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3 }} name="avg" connectNulls />
                  )}
                  {summaryMap.total && (
                    <Line type="monotone" dataKey="total" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="total" connectNulls />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Industry Dynamics Section — placed after cohort charts */}
          {cohortData?.industry_dynamics && (
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.25rem', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: '0 0 0.25rem' }}>
                Industry Dynamics — {allIndustries ? 'All Industries' : `NAICS ${naicsCodes}`} / {sizeCategory === 'All' ? 'All Sizes' : sizeCategory}
              </h3>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 1rem' }}>
                How the broader industry (same NAICS/size) evolved alongside this cohort — new plan entries, exits, and net growth rates.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Total Plans in Industry */}
                <div>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>Total Plans in Industry</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dynamicsChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                      <Tooltip formatter={(v) => [v?.toLocaleString(), 'Plans']} />
                      <Bar dataKey="totalPlans" fill="#6366f1" name="Total Plans" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Entry & Exit Rates */}
                <div>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>Entry & Exit Rates</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={dynamicsChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v.toFixed(0)}%`} />
                      <Tooltip formatter={(v) => [`${v?.toFixed(1)}%`]} />
                      <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                      <Line type="monotone" dataKey="entryRate" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} name="Entry Rate" connectNulls />
                      <Line type="monotone" dataKey="exitRate" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} name="Exit Rate" connectNulls />
                      <Line type="monotone" dataKey="netGrowth" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} name="Net Growth" connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* New vs Exited Plans */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>New vs Exited Plans</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dynamicsChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                      <Bar dataKey="newPlans" fill="#059669" name="New Plans" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="exitedPlans" fill="#dc2626" name="Exited Plans" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* No results state */}
      {cohortData && !loading && cohortData.total_plans === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
          <h3 style={{ color: '#111827', fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.5rem' }}>No Plans Found</h3>
          <p style={{ color: '#6b7280', maxWidth: '400px', margin: '0 auto' }}>
            No plans matched the selected criteria. Try a different NAICS code, size category, or effective year.
          </p>
        </div>
      )}
    </div>
  );
};

export default CohortAnalysis;
